import { GetApplicationDataResponse } from 'handlers/applicationData';

import Api, { ErrorMode, FetchMethodType } from './Api';

interface Calculation {
  term: number;
  apr: number;
  interestRate: number;
  baseInterestRate: number;
  fee: number;
  monthlyPayment: number;
  paymentAmount: number;
  totalLoanAmount: number;
  loanAmount: number;
  totalMonthsSaved?: number;
  totalInterestSavings?: number;
  achPaymentAmount?: number;
}

export interface Offer {
  tier: number;
  pricingSegment: number;
  term: number;
  calculations: Calculation[];
}

export interface SaveOfferData {
  offerData: {
    tier: number;
    term: number;
    loanAmount: number;
  };
}

export interface GetAmountPerPaycheck {
  amountPerPaycheck: Calculation;
}

export enum CreditScoreSource {
  Equifax = 'Equifax',
  Experian = 'Experian',
  TransUnion = 'TransUnion',
}

export interface CreditScoreHistoryEntry {
  date: string;
  score: number;
  source: CreditScoreSource;
}

export interface CreditScoreHistoryResponse {
  history: CreditScoreHistoryEntry[];
}

export interface ReportsApi {
  getOffers(applicationId: string): Promise<Offer[]>;
  getAmountPerPaycheck(applicationId: string, amount: number): Promise<GetAmountPerPaycheck>;
  saveSelectedOffer(applicationId: string, offerData: SaveOfferData): Promise<GetApplicationDataResponse>;
  getCreditScoreHistory(applicationId: string): Promise<CreditScoreHistoryResponse>;
}

export default class ReportsRestApi extends Api implements ReportsApi {
  public async getAmountPerPaycheck(applicationId: string, amount: number): Promise<GetAmountPerPaycheck> {
    return this.fetch<GetAmountPerPaycheck>(`/reports/amountPerPaycheck/${applicationId}`, {
      method: FetchMethodType.POST,
      body: { amount },
      errorMode: ErrorMode.DisplayGenericMessage,
    });
  }

  public async getOffers(applicationId: string): Promise<Offer[]> {
    return this.fetch<Offer[]>(`/reports/offer/${applicationId}`, {
      method: FetchMethodType.GET,
      errorMode: ErrorMode.DisplayGenericMessage,
    });
  }

  public async saveSelectedOffer(applicationId: string, data: SaveOfferData): Promise<GetApplicationDataResponse> {
    return this.fetch(`/reports/offer/${applicationId}`, {
      method: FetchMethodType.PATCH,
      body: data,
      errorMode: ErrorMode.DisplayGenericMessage,
    });
  }

  public async getCreditScoreHistory(applicationId: string): Promise<CreditScoreHistoryResponse> {
    return this.fetch<CreditScoreHistoryResponse>(`/reports/creditScoreHistory/${applicationId}`, {
      method: FetchMethodType.GET,
      errorMode: ErrorMode.HideError,
    });
  }
}
